import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  VStack,
  Input,
  Button,
  Text,
  Flex,
  useColorModeValue,
  Spinner,
  Avatar,
  HStack,
  IconButton,
  Badge,
  Collapse,
  Code,
  useDisclosure,
} from '@chakra-ui/react';
import {
  Send,
  Trash2,
  ChevronDown,
  ChevronUp,
  FileText,
  FileSpreadsheet,
  File,
} from 'lucide-react';
import { useChatEnhanced } from '../../hooks/useChatEnhanced';
import { ChatMessage, ChatSettings as ChatSettingsType, MessageHistory } from '../../types/chat';
import { ChartRenderer } from '../../components/Chat/ChartRenderer';
import { InsightCard } from '../../components/Chat/InsightCard';
import { DocumentCard } from '../../components/Chat/DocumentCard';
import { ChatSettings } from '../../components/Chat/ChatSettings';
import { STORAGE_KEYS } from '../../constants/Config';
import { exportToPDF, exportToExcel, exportToTXT } from '../../utils/chatExport';

const MAX_MESSAGES = 40; // 20 respostas (20 pares pergunta/resposta)

// Funções de persistência
const loadChatFromStorage = (): ChatMessage[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CHAT_MESSAGES);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Converter timestamps de string para Date
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao carregar chat do storage:', error);
  }
  return [];
};

const saveChatToStorage = (messages: ChatMessage[]) => {
  try {
    localStorage.setItem(STORAGE_KEYS.CHAT_MESSAGES, JSON.stringify(messages));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao salvar chat no storage:', error);
  }
};

const loadSettingsFromStorage = (): ChatSettingsType => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CHAT_SETTINGS);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao carregar configurações do storage:', error);
  }
  return {
    incluir_graficos: true,
    incluir_insights: true,
    incluir_documentos: false,
    debug_mode: false,
    ai_provider: 'backend',
  };
};

const saveSettingsToStorage = (settings: ChatSettingsType) => {
  try {
    localStorage.setItem(STORAGE_KEYS.CHAT_SETTINGS, JSON.stringify(settings));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Erro ao salvar configurações no storage:', error);
  }
};

export default function ChatEnhancedPage() {
  const [mensagens, setMensagens] = useState<ChatMessage[]>(() => loadChatFromStorage());
  const [texto, setTexto] = useState('');
  const [settings, setSettings] = useState<ChatSettingsType>(loadSettingsFromStorage);
  const isInitialMount = useRef(true);

  const { sendMessage } = useChatEnhanced();
  const chatBoxRef = useRef<HTMLDivElement>(null);

  // Salvar mensagens no storage sempre que mudarem (exceto na primeira carga)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveChatToStorage(mensagens);
  }, [mensagens]);

  // Salvar configurações no storage sempre que mudarem
  useEffect(() => {
    saveSettingsToStorage(settings);
  }, [settings]);

  // Auto-scroll para última mensagem
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [mensagens, sendMessage.isPending]);

  const handleEnviar = async () => {
    if (!texto.trim()) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'usuario',
      content: texto,
      timestamp: new Date(),
    };

    const mensagemUsuario = texto;

    // Preparar histórico antes de atualizar estado (inclui mensagem atual)
    const historicoAtualizado = [...mensagens, userMsg];
    const historico: MessageHistory[] = historicoAtualizado.slice(-5).map(msg => ({
      role: msg.role === 'usuario' ? 'user' : 'assistant',
      content: msg.content,
    }));

    // Aplicar limite de mensagens antes de adicionar
    setMensagens(prev => {
      const newMessages = [...prev, userMsg];
      // Manter apenas as últimas MAX_MESSAGES mensagens
      if (newMessages.length > MAX_MESSAGES) {
        return newMessages.slice(-MAX_MESSAGES);
      }
      return newMessages;
    });
    setTexto('');

    try {
      const resposta = await sendMessage.mutateAsync({
        mensagem: mensagemUsuario,
        historico,
        incluir_graficos: settings.incluir_graficos,
        incluir_insights: settings.incluir_insights,
        incluir_documentos: false,
        ai_provider: settings.ai_provider,
      });

      const assistantMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistente',
        content: resposta.resposta,
        graficos: resposta.graficos || undefined,
        insights: resposta.insights || undefined,
        documentos: resposta.documentos || undefined,
        dadosBrutos: resposta.dados_brutos || undefined,
        metadados: resposta.metadados,
        timestamp: new Date(),
      };

      setMensagens(prev => {
        const newMessages = [...prev, assistantMsg];
        // Manter apenas as últimas MAX_MESSAGES mensagens
        if (newMessages.length > MAX_MESSAGES) {
          return newMessages.slice(-MAX_MESSAGES);
        }
        return newMessages;
      });
    } catch (err: any) {
      // Extrai mensagem de erro da resposta do servidor ou do erro local
      let errorMessage = 'Tente novamente.';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }

      const errorMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistente',
        content: `Erro ao enviar mensagem: ${errorMessage}`,
        timestamp: new Date(),
      };
      setMensagens(prev => {
        const newMessages = [...prev, errorMsg];
        // Manter apenas as últimas MAX_MESSAGES mensagens
        if (newMessages.length > MAX_MESSAGES) {
          return newMessages.slice(-MAX_MESSAGES);
        }
        return newMessages;
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  const handleLimpar = () => {
    setMensagens([]);
    localStorage.removeItem(STORAGE_KEYS.CHAT_MESSAGES);
  };

  // Função auxiliar para processar placeholders em uma string
  const processPlaceholders = useCallback((text: string): React.ReactNode[] => {
    const parts: (string | React.ReactElement)[] = [];
    let lastIndex = 0;

    const PARA_PLACEHOLDER = '\u0001PARA\u0001';
    const LINE_PLACEHOLDER = '\u0002LINE\u0002';

    const placeholderRegex = new RegExp(`${PARA_PLACEHOLDER}|${LINE_PLACEHOLDER}`, 'g');
    let match = placeholderRegex.exec(text);

    while (match !== null) {
      // Texto antes do placeholder
      if (match.index > lastIndex) {
        const textBefore = text.substring(lastIndex, match.index);
        if (textBefore) {
          parts.push(textBefore);
        }
      }

      // Adicionar elemento React correspondente ao placeholder
      if (match[0] === PARA_PLACEHOLDER) {
        parts.push(
          <React.Fragment key={`para-${match.index}`}>
            <br />
            <br />
          </React.Fragment>,
        );
      } else if (match[0] === LINE_PLACEHOLDER) {
        parts.push(<br key={`line-${match.index}`} />);
      }

      lastIndex = placeholderRegex.lastIndex;
      match = placeholderRegex.exec(text);
    }

    // Texto restante
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    // Se não encontrou placeholders, retorna o texto original
    if (parts.length === 0) {
      return [text];
    }

    return parts.map((part, partIndex) => {
      if (React.isValidElement(part)) {
        return part;
      }
      const partId = `text-${partIndex}-${String(part).substring(0, 10)}`;
      return <span key={partId}>{String(part)}</span>;
    });
  }, []);

  const formatMessage = useCallback(
    (text: string): React.ReactNode => {
      if (!text) return null;

      let processed = String(text);

      // PASSO 1: Normalizar TODAS as variações dos marcadores (qualquer número de underscores)
      // Isso garante que _LINE_BREAK_, __LINE_BREAK__, ___LINE_BREAK___ etc. sejam todos normalizados
      processed = processed.replace(/_{1,}PARAGRAPH_BREAK_{1,}/g, '__PARAGRAPH_BREAK__');
      processed = processed.replace(/_{1,}LINE_BREAK_{1,}/g, '__LINE_BREAK__');

      // PASSO 2: Normalizar quebras de linha existentes e converter em marcadores
      processed = processed.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      processed = processed.replace(/\n{2,}/g, '__PARAGRAPH_BREAK__');
      processed = processed.replace(/\n/g, '__LINE_BREAK__');

      // PASSO 3: Substituir marcadores por placeholders únicos que não conflitam
      // Usamos caracteres de controle que não aparecem em texto normal
      const PARA_PLACEHOLDER = '\u0001PARA\u0001';
      const LINE_PLACEHOLDER = '\u0002LINE\u0002';

      processed = processed.replace(/__PARAGRAPH_BREAK__/g, PARA_PLACEHOLDER);
      processed = processed.replace(/__LINE_BREAK__/g, LINE_PLACEHOLDER);

      // PASSO 4: Dividir por formatação (*texto* e _texto_) sem quebrar os placeholders
      // Regex para capturar formatação
      const formatRegex = /(\*[^*]+\*|_[^_]+_(?![^_]))/g;
      const parts: string[] = [];
      let lastIndex = 0;
      let match = formatRegex.exec(processed);

      while (match !== null) {
        // Adiciona texto antes do marcador de formatação
        if (match.index > lastIndex) {
          const textBefore = processed.substring(lastIndex, match.index);
          if (textBefore) parts.push(textBefore);
        }
        // Adiciona o marcador de formatação
        parts.push(match[0]);
        lastIndex = formatRegex.lastIndex;
        match = formatRegex.exec(processed);
      }

      // Adiciona texto restante
      if (lastIndex < processed.length) {
        parts.push(processed.substring(lastIndex));
      }

      // Se não encontrou formatação, usa o texto completo
      if (parts.length === 0) {
        parts.push(processed);
      }

      // PASSO 5: Renderizar cada parte, convertendo placeholders em elementos React
      return parts
        .filter(part => part.length > 0)
        .map((part, index) => {
          const uniqueKey = `msg-${index}-${part.substring(0, 10)}-${Math.random().toString(36).substr(2, 9)}`;

          // Processar formatação (negrito e itálico) - conteúdo pode ter placeholders
          if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
            const content = part.slice(1, -1);
            // Processa placeholders diretamente sem recursão
            return <strong key={uniqueKey}>{processPlaceholders(content)}</strong>;
          }
          if (
            part.startsWith('_') &&
            part.endsWith('_') &&
            part.length > 2 &&
            !part.startsWith('__')
          ) {
            const content = part.slice(1, -1);
            return <em key={uniqueKey}>{processPlaceholders(content)}</em>;
          }

          // Processar placeholders no texto normal
          const placeholderContent = processPlaceholders(part);
          return <React.Fragment key={uniqueKey}>{placeholderContent}</React.Fragment>;
        });
    },
    [processPlaceholders],
  );

  return (
    <Box p={4} w="100%" h="85vh" maxW="100vw" overflow="hidden">
      <VStack spacing={4} align="stretch" h="100%">
        {/* Header */}
        <HStack justify="space-between" pb={2} borderBottomWidth="1px">
          <VStack align="start" spacing={0}>
            <Text fontSize="2xl" fontWeight="bold">
              Chat Inteligente
            </Text>
            <Text fontSize="sm" color="gray.500">
              Análise de dados com IA - Gráficos, Insights e Documentos
            </Text>
          </VStack>
          <HStack>
            <IconButton
              aria-label="Limpar chat"
              icon={<Trash2 size={18} />}
              size="sm"
              variant="ghost"
              onClick={handleLimpar}
              isDisabled={mensagens.length === 0}
            />
          </HStack>
        </HStack>

        {/* Chat Area */}
        <Box ref={chatBoxRef} flex="1" overflowY="auto" pr={2} maxW="100%">
          {mensagens.length === 0 && (
            <VStack spacing={4} mt={8} color="gray.500" textAlign="center">
              <Text fontSize="lg">👋 Olá! Como posso ajudar?</Text>
              <VStack align="start" spacing={2} fontSize="sm">
                <Text fontWeight="bold">Exemplos de perguntas:</Text>
                <Text>• &quot;Qual o faturamento total do mês passado?&quot;</Text>
                <Text>• &quot;Mostre a evolução de impostos nos últimos 6 meses&quot;</Text>
                <Text>• &quot;Quais empresas estão com impostos atrasados?&quot;</Text>
                <Text>• &quot;Compare o faturamento de outubro vs setembro&quot;</Text>
              </VStack>
            </VStack>
          )}

          {mensagens.map(msg => {
            const isUser = msg.role === 'usuario';
            return (
              <MessageBubble
                key={msg.id}
                message={msg}
                isUser={isUser}
                formatMessage={formatMessage}
                debugMode={settings.debug_mode}
              />
            );
          })}

          {sendMessage.isPending && (
            <Flex mt={2} justifyContent="flex-start">
              <Box
                bg={useColorModeValue('gray.100', 'gray.700')}
                px={4}
                py={2}
                borderRadius="md"
                maxW="70%"
              >
                <HStack>
                  <Spinner size="sm" />
                  <Text fontSize="sm">
                    {settings.ai_provider === 'claude'
                      ? 'Claude está pensando...'
                      : 'Analisando dados...'}
                  </Text>
                </HStack>
              </Box>
            </Flex>
          )}
        </Box>

        {/* Configurações em linha acima do input */}
        <Box borderTopWidth="1px" borderColor="gray.200" pt={3} pb={2}>
          <ChatSettings settings={settings} onChange={setSettings} compact />
        </Box>

        {/* Input Area */}
        <Flex gap={3} align="center">
          <Input
            placeholder="Digite sua mensagem..."
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={handleKeyDown}
            flex="1"
            isDisabled={sendMessage.isPending}
            borderRadius="xl"
            borderWidth="2px"
            borderColor="gray.200"
            _hover={{
              borderColor: 'blue.300',
            }}
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px #196ABB',
            }}
            bg="white"
            py={6}
            fontSize="sm"
          />
          <Button
            leftIcon={<Send size={18} />}
            onClick={handleEnviar}
            isDisabled={!texto.trim() || sendMessage.isPending}
            colorScheme="blue"
            borderRadius="xl"
            px={6}
            py={6}
            bg="blue.500"
            backgroundImage="linear-gradient(135deg, #196ABB 0%, #1B88F5 100%)"
            boxShadow="0 4px 12px rgba(25, 106, 187, 0.3)"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(25, 106, 187, 0.4)',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            transition="all 0.2s"
            fontWeight="semibold"
          >
            Enviar
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
}

// Componente auxiliar para renderizar mensagem
interface MessageBubbleProps {
  message: ChatMessage;
  isUser: boolean;
  // eslint-disable-next-line no-unused-vars
  formatMessage: (text: string) => React.ReactNode;
  debugMode: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isUser,
  formatMessage,
  debugMode,
}) => {
  const { isOpen: isDebugOpen, onToggle: onDebugToggle } = useDisclosure();

  return (
    <Flex mb={4} justifyContent={isUser ? 'flex-end' : 'flex-start'}>
      <VStack align={isUser ? 'flex-end' : 'flex-start'} maxW="80%" spacing={2}>
        {/* Avatar e Nome */}
        <HStack spacing={2}>
          {!isUser && (
            <Avatar
              size="sm"
              name="Assistente"
              src="https://cdn-icons-png.flaticon.com/512/4712/4712100.png"
            />
          )}
          <Text fontSize="xs" color="gray.500">
            {isUser ? 'Você' : 'Assistente IA'}
          </Text>
          {message.metadados && (
            <Badge fontSize="xs" colorScheme="gray">
              {message.metadados.tempo_processamento_segundos}s
            </Badge>
          )}
        </HStack>

        {/* Mensagem de Texto */}
        <Box
          bg={isUser ? 'blue.500' : useColorModeValue('white', 'gray.700')}
          backgroundImage={isUser ? 'linear-gradient(135deg, #196ABB 0%, #1B88F5 100%)' : undefined}
          color={isUser ? 'white' : 'inherit'}
          px={4}
          py={3}
          borderRadius="xl"
          maxW="100%"
          wordBreak="break-word"
          boxShadow={
            isUser ? '0 4px 12px rgba(25, 106, 187, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'
          }
          border={!isUser ? '1px solid' : 'none'}
          borderColor={!isUser ? useColorModeValue('gray.200', 'gray.600') : 'transparent'}
          transition="all 0.2s"
          _hover={{
            transform: 'translateY(-1px)',
            boxShadow: isUser
              ? '0 6px 16px rgba(25, 106, 187, 0.4)'
              : '0 4px 12px rgba(0, 0, 0, 0.12)',
          }}
        >
          {isUser ? (
            <Text fontSize="sm" lineHeight="1.6" whiteSpace="pre-wrap">
              {message.content}
            </Text>
          ) : (
            <Box as="div" fontSize="sm" lineHeight="1.6">
              {formatMessage(message.content)}
            </Box>
          )}
        </Box>

        {/* Botões de Exportação */}
        {!isUser && (
          <HStack spacing={2} mt={2}>
            <Button
              size="xs"
              variant="outline"
              colorScheme="red"
              leftIcon={<FileText size={14} />}
              onClick={() =>
                exportToPDF({
                  resposta: message.content,
                  dadosBrutos: message.dadosBrutos,
                  titulo: 'Análise do Chat',
                })
              }
            >
              PDF
            </Button>
            <Button
              size="xs"
              variant="outline"
              colorScheme="green"
              leftIcon={<FileSpreadsheet size={14} />}
              onClick={() =>
                exportToExcel({
                  resposta: message.content,
                  dadosBrutos: message.dadosBrutos,
                  titulo: 'Análise do Chat',
                })
              }
            >
              Excel
            </Button>
            <Button
              size="xs"
              variant="outline"
              colorScheme="gray"
              leftIcon={<File size={14} />}
              onClick={() =>
                exportToTXT({
                  resposta: message.content,
                  dadosBrutos: message.dadosBrutos,
                  titulo: 'Análise do Chat',
                })
              }
            >
              TXT
            </Button>
          </HStack>
        )}

        {/* Gráficos */}
        {message.graficos && message.graficos.length > 0 && (
          <VStack align="stretch" w="100%" spacing={2}>
            {message.graficos.map(chart => (
              <ChartRenderer key={`chart-${chart.titulo}`} chart={chart} />
            ))}
          </VStack>
        )}

        {/* Insights */}
        {message.insights && message.insights.length > 0 && (
          <VStack align="stretch" w="100%" spacing={2}>
            <Text fontSize="xs" fontWeight="bold" color="gray.500">
              💡 INSIGHTS ({message.insights.length})
            </Text>
            {message.insights.map(insight => (
              <InsightCard key={`insight-${insight.titulo}`} insight={insight} />
            ))}
          </VStack>
        )}

        {/* Documentos */}
        {message.documentos && message.documentos.length > 0 && (
          <VStack align="stretch" w="100%" spacing={2}>
            <Text fontSize="xs" fontWeight="bold" color="gray.500">
              📄 DOCUMENTOS ({message.documentos.length})
            </Text>
            {message.documentos.map(documento => (
              <DocumentCard key={`doc-${documento.titulo}`} documento={documento} />
            ))}
          </VStack>
        )}

        {/* Modo Debug */}
        {debugMode && message.metadados && !isUser && (
          <Box w="100%">
            <HStack
              cursor="pointer"
              onClick={onDebugToggle}
              color="gray.500"
              fontSize="xs"
              _hover={{ color: 'gray.700' }}
            >
              <Text fontWeight="bold">🐛 DEBUG INFO</Text>
              {isDebugOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </HStack>
            <Collapse in={isDebugOpen}>
              <VStack align="stretch" mt={2} spacing={2}>
                <Box
                  bg={useColorModeValue('gray.50', 'gray.800')}
                  p={2}
                  borderRadius="md"
                  fontSize="xs"
                >
                  <Text fontWeight="bold" mb={1}>
                    Metadados:
                  </Text>
                  <Code fontSize="xs">{JSON.stringify(message.metadados, null, 2)}</Code>
                </Box>
              </VStack>
            </Collapse>
          </Box>
        )}
      </VStack>
    </Flex>
  );
};
